import React from "react";
import useUsageData from "../../hooks/api/useUsageData";
import { Skeleton } from "@vibe/core";
import mondaySdk from "monday-sdk-js";
import UsageRing from "./UsageRing";
import { getMeterDisplay, MeterDisplay } from "./usageDisplay";
import { cn } from "../../utils/tailwind-merge";

const monday = mondaySdk();

const RING_COLOR = "#0273EA";
const RING_SIZE = 44;
const RING_STROKE_WIDTH = 10;
const STRIP_MIN_HEIGHT = "min-h-[4.75rem]";

// Cells sit side by side from 620px up and stack below it; the divider follows.
// The plan cell takes only the width it needs, so the two meters keep room for
// "limit reached" inside the item view's 640px column.
const CELL = "flex items-center gap-3 px-4 py-4 min-w-0";
const GRID = "grid grid-cols-1 min-[620px]:grid-cols-[1fr_1fr_auto]";
const CELL_DIVIDER =
  "border-0 border-solid border-[var(--layout-border-color)] border-t min-[620px]:border-t-0 min-[620px]:border-l";

const UsageMeter = ({
  title,
  percent,
  unlimited,
  display,
  className,
}: {
  title: string;
  percent: number;
  unlimited: boolean;
  display: MeterDisplay;
  className?: string;
}) => (
  <div className={cn(CELL, className)}>
    <div className="flex-shrink-0" role="img" aria-label={display.ariaLabel}>
      <UsageRing
        percent={percent}
        color={RING_COLOR}
        size={RING_SIZE}
        strokeWidth={RING_STROKE_WIDTH}
        unlimited={unlimited}
        label={display.ringLabel}
      />
    </div>
    <div className="flex flex-col justify-center min-w-0">
      <span className="!font-figtree text-base font-medium leading-[1.375rem] truncate">
        {title}
      </span>
      <span
        className={cn(
          "!font-figtree text-sm leading-5",
          display.atLimit ? "text-[var(--negative-color)]" : "text-light-secondary-text-color",
        )}
      >
        {display.subtext}
      </span>
    </div>
  </div>
);

/**
 * Usage strip at the top of the item view. The wrapping card in home.tsx supplies
 * the surface, border and radius; this only lays out the three cells.
 */
const UsageSection = () => {
  const { isLoading, error, usageData } = useUsageData();

  if (isLoading) {
    return (
      <div className={cn("flex items-center px-4 py-2", STRIP_MIN_HEIGHT)}>
        <Skeleton fullWidth height={48} />
      </div>
    );
  }

  if (error || !usageData) {
    return (
      <div className={cn("flex items-center justify-center px-4 py-2", STRIP_MIN_HEIGHT)}>
        <p className="text-light-secondary-text-color">
          Error loading usage data. Please try again later.
        </p>
      </div>
    );
  }

  const openPlanSelection = () => {
    monday.execute("openPlanSelection", {
      isInPlanSelection: false,
    });
  };

  const unlimited = usageData.isUnlimited === true;

  return (
    <div className={cn(GRID, STRIP_MIN_HEIGHT)}>
      <UsageMeter
        title="Shared items"
        percent={usageData.usagePercentItems}
        unlimited={unlimited}
        display={getMeterDisplay(
          usageData.itemUsage,
          usageData.maxItems,
          usageData.usagePercentItems,
          unlimited,
          "items",
        )}
      />

      <UsageMeter
        title="Shared boards"
        percent={usageData.usagePercentBoards}
        unlimited={unlimited}
        className={CELL_DIVIDER}
        display={getMeterDisplay(
          usageData.boardUsage,
          usageData.maxBoards,
          usageData.usagePercentBoards,
          unlimited,
          "boards",
        )}
      />

      {/* Plan */}
      <div className={cn(CELL, CELL_DIVIDER, "flex-col items-start justify-center gap-0")}>
        <span className="!font-figtree text-base font-medium leading-[1.375rem] truncate max-w-full">
          {usageData.planName}
        </span>
        {unlimited ? (
          <span className="!font-figtree text-sm leading-5 text-light-secondary-text-color">
            Unlimited items and boards
          </span>
        ) : (
          <button
            type="button"
            onClick={openPlanSelection}
            className="!font-figtree text-sm leading-5 font-medium text-primary-color cursor-pointer bg-transparent border-0 p-0"
          >
            Upgrade Plan
          </button>
        )}
      </div>
    </div>
  );
};

export default UsageSection;
